import { createAction } from 'redux-actions';
import isEmail from 'validator/lib/isEmail';
import { push, replace } from 'react-router-redux';

import { closeMessageModal, openMessageModal } from 'actions/modal';
import { wrapFetch, wrapFetchAuth } from 'actions/endpoint';
import { getUser } from 'actions/user';
import { openFailedAlert, openSuccessAlert } from 'actions/alert';

import authStorage from 'util/authStorage';
import { examPassword } from 'util/exam';
import { t, languageMapRoute } from 'util/i18n';

const updateAccessToken = createAction(
	'UPDATE_ACCESS_TOKEN',
	({ status, message, data }, remember) => {
		const token = {
			accessToken: '',
			endAt: '',
			expiresIn: '',
			refreshToken: '',
		};

		if (status === 200 || status === 201) {
			const expireMS = data.expires_in * 1000;

			token.accessToken = data.access_token;
			token.endAt = new Date().getTime() + expireMS;
			token.expiresIn = expireMS;
			token.refreshToken = data.refresh_token;
		}

		authStorage.setStoreInLocalStorage(remember);

		authStorage.setToken(JSON.stringify(token));

		return {
			token,
			status,
			message,
		};
	},
);

export const signupNormal = createAction('SIGNUP_NORMAL', () => async (dispatch, getState) => {
	const {
		auth: { signupForm: { email, password, checkPassword, agreement } },
		routing: { locationBeforeTransitions: { query } },
	} = getState();

	if (email.value === '' || !isEmail(email.value)) {
		dispatch(openFailedAlert('loginSignup', t('modalLoginSignup:verifyEmailFail')));
		return;
	}

	if (!examPassword(password.value)) {
		dispatch(openFailedAlert('loginSignup', t('modalLoginSignup:verifyPasswordFail')));
		return;
	}

	if (password.value !== checkPassword.value) {
		dispatch(openFailedAlert('loginSignup', t('modalLoginSignup:verifyCheckPasswordFail')));
		return;
	}

	if (agreement.value === false) {
		dispatch(openFailedAlert('loginSignup', t('modalLoginSignup:verifyAgree')));
		return;
	}

	const result = await wrapFetch('auth/signUp', {
		method: 'POST',
		body: JSON.stringify({
			email: email.value,
			password: password.value,
			vip_token: query.vip_token,
		}),
	});

	if (result.status !== 200 && result.status !== 201) {
		if (result.message === 'email repeat') {
			dispatch(openFailedAlert('loginSignup', t('modalLoginSignup:SignupLoginFail')));
		} else {
			dispatch(openFailedAlert('loginSignup', result.message));
		}
		return;
	}

	dispatch(openMessageModal('loginSignup', { type: 'signupSuccess' }));
});

export const loginNormal = createAction('LOGIN_NORMAL', () => async (dispatch, getState) => {
	const { auth: { loginForm: { email, password, remember } } } = getState();

	if (email.value === '' || !isEmail(email.value)) {
		dispatch(openFailedAlert('loginSignup', t('modalLoginSignup:verifyEmailFail')));
		return;
	}

	const result = await wrapFetch('auth/login', {
		method: 'POST',
		body: JSON.stringify({ email: email.value, password: password.value }),
	});

	dispatch(updateAccessToken(result, remember.value));

	if (result.status !== 200 && result.status !== 201) {
		if (result.message === 'login failed') {
			dispatch(openFailedAlert('loginSignup', t('modalLoginSignup:SignupLoginFail')));
		} else if (result.message === 'need verify') {
			dispatch(
				openFailedAlert('loginSignup', t('modalLoginSignup:verifyFail'), {
					type: 'resendVerifyEmail',
				}),
			);
		} else {
			dispatch(openFailedAlert('loginSignup', result.message));
		}
		return;
	}

	dispatch(closeMessageModal('loginSignup'));

	// Fetch user data after login
	await dispatch(getUser());

	const { routing: { locationBeforeTransitions: { pathname } } } = getState();

	// Specfic page for smart redirect
	if (pathname.includes('/login')) {
		const { routing: { locationBeforeTransitions: { state } } } = getState();

		if (state && !state.nextPathname.includes('/login')) {
			dispatch(replace({ pathname: state.nextPathname, query: state.nextQuery }));
			return;
		}

		dispatch(replace({ pathname: '/' }));
	}
});

export const forgetPassword = createAction('FORGET_PASSWORD', () => async (dispatch, getState) => {
	const { auth: { forgetForm: { email } } } = getState();

	const { status, message } = await wrapFetch('auth/forgetPassword', {
		method: 'POST',
		body: JSON.stringify({ email: email.value }),
	});

	if (status !== 200) {
		dispatch(openFailedAlert('loginSignup', message));
	}

	dispatch(
		openSuccessAlert(
			'loginSignup',
			t('modalLoginSignup:forgetPasswordSendSuccess', { email: email.value }),
		),
	);
});

export const setNewPassword = createAction('SET_NEW_PASSWORD', () => async (dispatch, getState) => {
	const {
		auth: { passwordForm: { password, checkPassword } },
		routing: { locationBeforeTransitions: { query: { token }, pathname } },
	} = getState();

	if (!examPassword(password.value)) {
		dispatch(openFailedAlert('loginSignup', t('modalLoginSignup:verifyPasswordFail')));
		return;
	}

	if (password.value !== checkPassword.value) {
		dispatch(openFailedAlert('loginSignup', t('modalLoginSignup:verifyCheckPasswordFail')));
		return;
	}

	if (pathname.includes('signup')) {
		const result = await wrapFetch('auth/setVipPassword', {
			method: 'POST',
			body: JSON.stringify({
				password: password.value,
				token,
			}),
		});

		dispatch(updateAccessToken(result, true));

		if (result.status !== 200) {
			if (result.message === 'not found vip') {
				dispatch(openFailedAlert('loginSignup', t('modalLoginSignup:vipTokenFailed')));
				return;
			}
			dispatch(openFailedAlert('loginSignup', result.message));
			return;
		}

		dispatch(closeMessageModal('loginSignup'));

		// Fetch user data after login
		await dispatch(getUser());

		const { i18n: { lan } } = getState();

		dispatch(replace({ pathname: `${languageMapRoute(lan)}/user/settings` }));
	} else {
		const { status, message } = await wrapFetch('auth/resetPassword', {
			method: 'POST',
			body: JSON.stringify({
				password: password.value,
				password_confirmation: checkPassword.value,
				password_token: token,
			}),
		});

		if (status !== 200) {
			if (message === 'The payload is invalid.') {
				dispatch(openFailedAlert('loginSignup', '重設密碼連結失效，請重新索取'));
				return;
			}
			dispatch(openFailedAlert('loginSignup', message));
			return;
		}

		dispatch(openSuccessAlert('loginSignup', '新密碼設定好了！來登入吧'));
		dispatch(openMessageModal('loginSignup', { type: 'loginForm' }));
	}
});

export const verifyEmail = createAction('VERIFY_EMAIL', token => async dispatch => {
	const { status } = await wrapFetch('auth/verify', {
		method: 'POST',
		body: JSON.stringify({ email_token: token }),
	});

	if (status !== 200 && status !== 201) {
		dispatch(openMessageModal('loginSignup', { type: 'loginForm' }));
		dispatch(openFailedAlert('loginSignup', 'token 錯誤'));
		return;
	}

	dispatch(openMessageModal('loginSignup', { type: 'loginForm' }));
	dispatch(openSuccessAlert('loginSignup', t('modalLoginSignup:verifySuccess')));
});

export const logout = createAction('LOGOUT', () => async (dispatch, getState) => {
	const { status } = await wrapFetchAuth('user/logout', {
		method: 'POST',
	});

	if (status !== 200) {
		dispatch(openFailedAlert('generic', 'Logout Failed'));
	}

	authStorage.removeToken();

	const { routing: { locationBeforeTransitions: { pathname } } } = getState();

	if (
		pathname.includes('/user') ||
		pathname.includes('/booking') ||
		pathname.includes('/reservation')
	) {
		dispatch(push('/'));
	}
});

export const resendVerifyEmail = createAction(
	'RESEND_VERIFY_EMAIL',
	() => async (dispatch, getState) => {
		const { auth: { loginForm: { email } } } = getState();

		const { status } = await wrapFetch('auth/resendVerify', {
			method: 'POST',
			body: JSON.stringify({ email }),
		});

		if (status !== 200) {
			dispatch(openFailedAlert('loginSignup', 'Resend Faild'));
		}

		dispatch(openSuccessAlert('loginSignup', '認證信已寄出'));
	},
);



// WEBPACK FOOTER //
// ./src/actions/auth.js